// React Dependencies
import { React } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-confirm-alert/src/react-confirm-alert.css'; // Import css
import { useDispatch } from 'react-redux';
import loadable from '@loadable/component';
// API
import {
  ForgetPasswordPage,
  LoginPage,
  OtpVerificationPage,
  ResetPasswordPage
} from '../pages/Auth';
import { useGetSettingData } from '@hooks';
import { Loader } from '../../common/components';
// Routes
import PublicRoute from './PublicRoute';
import PrivateRoute from './PrivateRoute';
// Components
import { addSetting, settingData } from '../store/features/settingSlice';
import '@assets/scss/page/_editprofile.scss';
const EditProfilePage = loadable(
  () =>
    import('../pages/Accounts/EditProfile/EditProfilePage').then((module) => ({
      default: module.EditProfilePage
    })),
  {
    fallback: <Loader />
  }
);

const DashboardPage = loadable(
  () =>
    import('../pages/Dashboard/DashboardPage').then((module) => ({
      default: module.DashboardPage
    })),
  {
    fallback: <Loader />
  }
);

const ChangePasswordPage = loadable(
  () =>
    import('../pages/Accounts/ChangePassword/ChangePasswordPage').then((module) => ({
      default: module.ChangePasswordPage
    })),
  {
    fallback: <Loader />
  }
);

import { SetPasswordPage } from '../pages/Auth/SetPassword/SetPasswordPage';
import EmailTemplateRoutes from '../pages/Masters/EmailTemplate/EmailTemplateRoutes';
import TranslationRoutes from '../pages/Masters/Translations/TranslationRoutes';
import PageNotFound from '@common/components/PageNotFound';
import FaqRoute from '../pages/Masters/Faq/FaqRoute';
import FaqCategoryRoute from '../pages/Masters/FaqCategory/FaqCategoryRoute';
import RoleRoutes from '../pages/UsersMaster/Roles/RoleRoutes';
import DBFieldTypeRoute from '../pages/Projects/DBFieldType/DBFieldTypeRoute';
import AttendanceRoute from '../pages/HRMS/Attendance/AttendanceRoute';
import PunchInOutRoute from '../pages/HRMS/PunchInOut/PunchInOutRoute';
import ProjectRoute from '../pages/Projects/Project/ProjectRoute';
import UserRoutes from '../pages/UsersMaster/Users/UserRoutes';
import DepartmentRoute from '../pages/HRMS/Department/DepartmentRoute';
import BranchRoute from '../pages/HRMS/Branch/BranchRoute';
import HolidaysEtcRoute from '../pages/HRMS/HolidaysEtc/HolidaysEtcRoute';
import SettingRoutes from '../pages/Setting/SettingRoutes';
import JobsPositionRoute from '../pages/Recruitment/Jobs-Position/JobsPositionRoute';
import SkillRoute from '../pages/Recruitment/Skills/SkillRoute';
import InterviewStatusRoute from '../pages/Recruitment/InterviewStatus/InterviewStatusRoute';
import CitiesRoute from '../pages/Recruitment/Cities/CitiesRoute';
import SourcesRoute from '../pages/Recruitment/Sources/SourcesRoute';
import PublicReportsRoutes from '../pages/Reports/Public/PublicReportsRoutes';

import CompaniesRoute from '../pages/Recruitment/Companies/CompaniesRoute';
import PrivateReportsRoutes from '../pages/Reports/Private/PrivateReportsRoutes';
import ViewEditProfile from '../pages/Accounts/EditProfile/ViewEditProfile';
import AddressDetailsPage from '../pages/Accounts/Address Details/AddressDetailsPage';
import ContactAdditionalInfo from '../pages/Accounts/ContactAndAdditionalInfo/ContactAdditionalInfo';
import DepartmentDesignation from '../pages/Accounts/DepartmentAndDesignation/DepartmentDesignation';
import OtherPage from '../pages/Accounts/Other/OtherPage';
import MyAllTasksRoute from '../pages/Projects/MyAllTasks/MyAllTasksRoute';
import { useAlert } from '../../provider/useAlert';
import AllActivityRoute from '../pages/Projects/AllActivities/AllActivityRoute';
import HRScoringParametersRoute from '../pages/HRMS/HRScoringParameters/HRScoringParametersRoute';
import HrScoringRoute from '../pages/HRMS/HrScoring/HrScoringRoute';
import SkillScoringParametersRoute from '../pages/HRMS/SkillScoringParameters/SkillScoringParametersRoute';
import SkillsScoringOptionsRoute from '../pages/HRMS/SkillsScoringOptions/SkillsScoringOptionsRoute';
import UserSkillsScoreRoutes from '../pages/UsersMaster/Users/UserSkillsScore/UserSkillsScoreRoutes';
import MyUserSkillsScore from '../pages/Accounts/MyUserSkillsScore/MyUserSkillsScore';
import MyUserViewSkillScoreProgress from '../pages/Accounts/MyUserSkillsScore/MyUserViewSkillScoreProgress';
import AllSprintRoute from '../pages/Projects/AllSprint/AllSprintRoute';

const PagesRoutes = ({ t }) => {
  const dispatch = useDispatch();
  useGetSettingData(({ data: setting }) => {
    const dataStore = {
      dashboard_logo: setting.common_header_logo,
      front_logo: setting.common_login_logo,
      email_logo: setting?.common_email_header_logo,
      favicon_icon: setting?.common_favicon_icon,
      seo_title: setting.seo_title,
      seo_description: setting.seo_description,
      setting_get: false
    };
    dispatch(addSetting(dataStore));
  });
  const getSettingData = useSelector(settingData);
  const alert = useAlert();

  return (
    <Router basename={'/'}>
      <Helmet>
        <title>Technource ERP</title>
        <meta name="title" content="Technource ERP" />
        <meta name="description" content="Technource ERP" />
        <meta name="og:title" content="Technource ERP" />
        <meta name="og:description" content="Technource ERP" />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="icon" type="image/x-icon" href={`${getSettingData.favicon_icon}`} />
      </Helmet>
      <Routes>
        <Route element={<PublicRoute />}>
          <Route exact path="/" element={<LoginPage t={t} alert={alert} />} />
          <Route exact path="/login" element={<LoginPage t={t} alert={alert} />} />
          <Route
            exact
            path="/forget-password"
            element={<ForgetPasswordPage t={t} alert={alert} />}
          />
          <Route
            exact
            path="/otp-verification"
            element={<OtpVerificationPage t={t} alert={alert} />}
          />
          <Route exact path="/reset-password" element={<ResetPasswordPage t={t} alert={alert} />} />
          <Route
            exact
            path="/set-password/:user_id"
            element={<SetPasswordPage t={t} alert={alert} />}
          />
        </Route>
        <Route element={<PrivateRoute />}>
          <Route exact path="/dashboard" element={<DashboardPage t={t} alert={alert} />} />
          {/* My Proflie */}
          <Route exact path="/my-profile" element={<ViewEditProfile t={t} alert={alert} />} />
          {/* Edit Profile Rountes */}
          <Route exact path="/edit-profile" element={<EditProfilePage t={t} alert={alert} />} />
          {/* Change Password Rountes */}
          <Route
            exact
            path="/my-profile/change-password"
            element={<ChangePasswordPage t={t} alert={alert} />}
          />
          {/* Address Details Rountes */}
          <Route
            exact
            path="/my-profile/address-details"
            element={<AddressDetailsPage t={t} alert={alert} />}
          />
          {/* Contact & Additional Info Rountes */}
          <Route
            exact
            path="/my-profile/contact-additional-info"
            element={<ContactAdditionalInfo t={t} alert={alert} />}
          />
          {/* Department & Designation Rountes */}
          <Route
            exact
            path="/my-profile/department-designation"
            element={<DepartmentDesignation t={t} alert={alert} />}
          />
          {/* Skills Rountes */}          
          <Route
            exact
            path="/my-profile/skills"
            element={<MyUserSkillsScore t={t} />}
          />
          <Route
            exact
            path="/my-profile/skills/:skillScoringParametersId"
            element={<MyUserViewSkillScoreProgress t={t} />}
          />
          {/* My Profile Other Page Route */}
          <Route exact path="/my-profile/other" element={<OtherPage t={t} alert={alert} />} />
          {/* Roles Master   */}
          <Route exact path="/roles/*" element={<RoleRoutes t={t} alert={alert} />} />
          {/* user master */}
          <Route exact path="/user/*" element={<UserRoutes t={t} alert={alert} />} />
          {/* {shouldShowRoute('setting.index') && */}
          {/* } */}
          <Route exact path="/setting/*" element={<SettingRoutes t={t} alert={alert} />} />
          {/* Translations Routes */}
          <Route path="/translations/*" element={<TranslationRoutes t={t} alert={alert} />} />
          {/* Master Email Template Routes */}
          <Route path="/email-template/*" element={<EmailTemplateRoutes t={t} alert={alert} />} />
          {/* Faq Routes */}
          <Route exact path="/faq/*" element={<FaqRoute t={t} alert={alert} />} />
          {/* Faq Category Routes */}
          <Route exact path="/faq-category/*" element={<FaqCategoryRoute t={t} alert={alert} />} />
          {/* DBField Type Route */}
          <Route exact path="/dbfieldtype/*" element={<DBFieldTypeRoute t={t} alert={alert} />} />
          {/* My All Tasks Route */}
          <Route exact path="/myalltasks/*" element={<MyAllTasksRoute t={t} alert={alert} />} />
          {/* All Activities Route */}
          <Route exact path="/all-activities/*" element={<AllActivityRoute t={t} />} />
          {/* Project Route */}
          <Route exact path="/projects/*" element={<ProjectRoute t={t} alert={alert} />} />
          {/* HRMS Route */}
          <Route exact path="/attendance/*" element={<AttendanceRoute t={t} alert={alert} />} />
          {/* HR Scoring Route */}
          <Route exact path="/hr-scoring/*" element={<HrScoringRoute t={t} />} />
          {/* Punch In Out Route */}
          <Route exact path="/punch-in-out/*" element={<PunchInOutRoute t={t} alert={alert} />} />
          {/* branch route */}
          <Route exact path="/branch/*" element={<BranchRoute t={t} alert={alert} />} />
          {/* Holidays Etc route */}
          <Route exact path="/holidays-etc/*" element={<HolidaysEtcRoute t={t} alert={alert} />} />
          {/* department route */}
          <Route exact path="/department/*" element={<DepartmentRoute t={t} alert={alert} />} />
          {/* Job Position */}
          <Route exact path="/job-position/*" element={<JobsPositionRoute t={t} alert={alert} />} />
          {/* HR Scoring Parameters */}
          <Route exact path="/hr-scoring-parameters/*" element={<HRScoringParametersRoute t={t} alert={alert} />} />
          {/* Skill Scoring Parameter route */}
          <Route exact path="/skill-scoring-parameters/*" element={<SkillScoringParametersRoute t={t} />} />
          {/* Skills Scoring Options route */}
          <Route exact path="/skills-scoring-options/*" element={<SkillsScoringOptionsRoute t={t} />} />
          {/* Skills Scoring Options route */}
          <Route exact path="/user-skills-score/*" element={<UserSkillsScoreRoutes t={t} />} />

          {/* All-sprint Route */}
          <Route exact path="/all-sprints/*" element={<AllSprintRoute t={t} alert={alert} />} />
          {/* recruitment candidate companies route */}
          <Route
            exact
            path="/recruitment-candidate-companies/*"
            element={<CompaniesRoute t={t} alert={alert} />}
          />
          {/* recruitment candidate companies route */}

          {/* recruitment candidate skills route */}
          <Route
            exact
            path="/recruitment-candidate-skills/*"
            element={<SkillRoute t={t} alert={alert} />}
          />
          {/* recruitment candidate skills route */}

          {/* recruitment candidate cities route */}
          <Route exact path="/recruitment-cities/*" element={<CitiesRoute t={t} alert={alert} />} />
          {/* recruitment candidate cities route */}

          {/* recruitment candidate sources route */}
          <Route
            exact
            path="/recruitment-candidate-sources/*"
            element={<SourcesRoute t={t} alert={alert} />}
          />
          {/* recruitment candidate sources route */}

          {/* Recruitment Interview Status */}
          <Route
            exact
            path="/recruitment-interview-status/*"
            element={<InterviewStatusRoute t={t} alert={alert} />}
          />
          {/* Recruitment Interview Status */}
          {/* Public Reports */}
          <Route
            exact
            path="/reports/public/*"
            element={<PublicReportsRoutes t={t} alert={alert} />}
          />
          {/* Public Reports */}

          {/* private Reports */}
          <Route
            exact
            path="/reports/private/*"
            element={<PrivateReportsRoutes t={t} alert={alert} />}
          />
          {/* private Reports */}
        </Route>
        <Route>
          <Route path="*" element={<PageNotFound t={t} from="admin" />} />
        </Route>
      </Routes>
    </Router>
  );
};
PagesRoutes.propTypes = {
  t: PropTypes.func
};
export default PagesRoutes;
